import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormControl, ValidatorFn, AbstractControl, FormGroupDirective } from '@angular/forms';
import { ILicenseEmployer } from 'app/models/licenses/license-employer';
import { IEmployer } from 'app/models/licenses/employer';
import { LicenseEmployerService } from 'app/services/license/license-employer.service';
import { MatTableDataSource } from '@angular/material/table';
import { DatePipe } from '@angular/common';
import { SharedService } from '@shared/services/shared.service';

@Component({
  selector: 'app-employer',
  templateUrl: './employer.component.html',
  styleUrls: ['./employer.component.scss'],
  providers: [DatePipe]
})

export class EmployerComponent implements OnInit {
  @Input() licenseMasterId: number = 0;
  @Input() isLocked: boolean = false;
  @Input() isRenewal: boolean = false;
  @Output() formValidityChange = new EventEmitter<boolean>();
  @ViewChild(FormGroupDirective) formDirective!: FormGroupDirective;

  public employerForm = new FormGroup({
    employerName: new FormControl('', Validators.required),
    otherName: new FormControl(''),
    otherLocation: new FormControl(''),
    fromDate: new FormControl('', Validators.required),
    toDate: new FormControl('')
  }, {
    validators: [
      this.otherLocationValidator(),
      this.otherNameValidator()
    ]
  });

  public editEmployerForm = new FormGroup({
    employerName: new FormControl('', Validators.required),
    otherName: new FormControl(''),
    otherLocation: new FormControl(''),
    fromDate: new FormControl<Date | null>(null, Validators.required),
    toDate: new FormControl<Date | null>(null)
  }, {
    validators: [
      this.otherLocationValidator(),
      this.otherNameValidator()
    ]
  });

  public employers: IEmployer[] = [];
  public selectedEmployer: ILicenseEmployer = {
    id: 0,
    employerId: 0,
    licenseMasterId: this.licenseMasterId,
    employerName: '',
    otherName: '',
    otherLocation: '',
    location: '',
    fromDate: '',
    toDate: ''
  }
  public licenseEmployers: ILicenseEmployer[] = [];
  public isEditing: boolean = false;
  public isOtherSelected = false;
  public editingIndex: number = -1;

  public displayedColumns: string[] = ['employerName', 'location', 'fromDate', 'toDate', 'actions'];
  public dataSource = new MatTableDataSource<ILicenseEmployer>();

  public employerService = inject(LicenseEmployerService);
  public sharedService = inject(SharedService);

  ngOnInit() {
    this.getEmployers();
    this.getLicenseEmployers();
  }

  getEmployers(): void {
    this.employerService.getEmployers().subscribe({
      next: response => this.employers = response,
      error: e => console.log('Error getting employers:', e)
    });
  }

  getLicenseEmployers(): void {
    this.employerService.getLicenseEmployers(this.licenseMasterId).subscribe({
      next: response => this.licenseEmployers = response,
      error: e => console.log('Error getting employment history:', e),
      complete: () => this.updateEmployerTable()
    });
  }

  updateEmployerTable(): void {
    this.dataSource.data = this.licenseEmployers;
  }

  addEmployer(): void {
    let form = this.employerForm.getRawValue();
    const isOther = form.employerName === 'Other';
    let employerId = 0;
    let location = form.otherLocation;
    if(!isOther){
      employerId = this.employers.find(e => e.employerName === form.employerName)?.id || 0;
      location = this.employers.find(e => e.employerName === form.employerName)?.location || form.otherLocation;
    }

    const newEmployer: ILicenseEmployer = {
      id: 0,
      employerId: employerId,
      licenseMasterId: this.licenseMasterId,
      employerName: form.employerName,
      otherName: form.otherName || '',
      otherLocation: form.otherLocation || '',
      location: location,
      fromDate: form.fromDate,
      toDate: form.toDate
    };

    this.employerService.createLicenseEmployer(newEmployer).subscribe({
      next: response => this.licenseEmployers.push(response),
      error: e => console.log('Error creating employer:', e),
      complete: () => {
        this.updateEmployerTable();
        this.employerForm.reset();
        this.formDirective.resetForm();
      }
    });
  }

  editEmployer(employer: ILicenseEmployer, index: number) {
    this.editingIndex = index;
    this.selectedEmployer = employer;
    this.editEmployerForm.patchValue({
      employerName: employer.employerName,
      otherName: employer.otherName,
      otherLocation: employer.otherLocation
    });

    if(employer.fromDate){
      this.editEmployerForm.get('fromDate')?.setValue(new Date(employer.fromDate));
    }
    if(employer.toDate){
      this.editEmployerForm.get('toDate')?.setValue(new Date(employer.toDate));
    }

  }

  cancelEdit(): void {
    this.editEmployerForm.reset();
    this.editEmployerForm.markAsPristine();
    this.editEmployerForm.markAsUntouched();
    this.editingIndex = -1;
    this.selectedEmployer = this.getEmptyEmployer();
  }

  saveEmployer(): void {
    let form = this.editEmployerForm.getRawValue();
    const isOther = form.employerName === 'Other';
    let employerId = 0;
    let location = form.otherLocation;
    if(!isOther){
      employerId = this.employers.find(e => e.employerName === form.employerName)?.id || 0;
      location = this.employers.find(e => e.employerName === form.employerName)?.location || form.otherLocation;
    }

    this.selectedEmployer.employerId = employerId;
    this.selectedEmployer.employerName = form.employerName;
    this.selectedEmployer.otherName = form.otherName || '';
    this.selectedEmployer.otherLocation = form.otherLocation || '';
    this.selectedEmployer.location = location;
    this.selectedEmployer.fromDate = form.fromDate?.toDateString() ?? null;
    this.selectedEmployer.toDate = form.toDate?.toDateString() ?? null;

    this.employerService.updateLicenseEmployer(this.selectedEmployer).subscribe({
      next: response => {
        if(response){
          let index = this.licenseEmployers.findIndex(e => e.id === this.selectedEmployer.id);
          if(index !== -1){
            this.licenseEmployers[index] = this.selectedEmployer;
          }
          this.updateEmployerTable();
        }
      },
      error: e => console.log('Error updating employer:', e),
      complete: () => {
        this.editEmployerForm.reset();
        this.editEmployerForm.markAsPristine();
        this.editEmployerForm.markAsUntouched();
        this.editingIndex = -1;
        this.selectedEmployer = this.getEmptyEmployer();
      }
    });
  }

  getEmptyEmployer(): ILicenseEmployer {
    return {
      id: 0,
      employerId: 0,
      licenseMasterId: this.licenseMasterId,
      employerName: '',
      otherName: '',
      otherLocation: '',
      location: '',
      fromDate: '',
      toDate: ''
    };
  }

  deleteEmployer(employer: ILicenseEmployer) {
    const name = employer.employerName === 'Other' ? employer.otherName : employer.employerName;
    this.sharedService.openConfirm(`Are you sure you want to delete ${name}?`);
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.employerService.deleteLicenseEmployer(employer.id).subscribe({
            next: () => {
              this.licenseEmployers = this.licenseEmployers.filter(e => e.id !== employer.id);
              this.updateEmployerTable();
              console.log('Employer deleted successfully');
            },
            error: e => console.log('Error deleting employer:', e)
          });
        }
      }
    );
  }

  validateEmploymentHistory(): string[] {
    let errors: string[] = [];
    if (this.licenseEmployers.length === 0) {
      errors.push('Employment history must have at least one record.');
    }
    return errors;
  }

  otherNameValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const otherName = control.value.otherName;
      const employerName = control.value.employerName;

      if(employerName !== 'Other'){
        return null;
      }
      return otherName ? null : { otherNameRequired: true };
    }
  }

  validateOtherName(): boolean {
    if (this.employerForm.hasError('otherNameRequired') && this.employerForm.get('otherName')?.touched) {
      this.employerForm.get('otherName')?.setErrors(['required']);
      return true;
    }
    return false;
  }

  validateOtherNameEdit(): boolean {
    if (this.editEmployerForm.hasError('otherNameRequired') && this.editEmployerForm.get('otherName')?.touched) {
      this.editEmployerForm.get('otherName')?.setErrors(['required']);
      return true;
    }
    return false;
  }

  otherLocationValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const otherLocation = control.value.otherLocation;
      const employerName = control.value.employerName;

      if(employerName !== 'Other'){
        return null;
      }
      return otherLocation ? null : { otherLocationRequired: true };
    }
  }

  validateOtherLocation(): boolean {
    if (this.employerForm.hasError('otherLocationRequired') && this.employerForm.get('otherLocation')?.touched) {
      this.employerForm.get('otherLocation')?.setErrors(['required']);
      return true;
    }
    return false;
  }

  validateOtherLocationEdit(): boolean {
    if (this.editEmployerForm.hasError('otherLocationRequired') && this.editEmployerForm.get('otherLocation')?.touched) {
      this.editEmployerForm.get('otherLocation')?.setErrors(['required']);
      return true;
    }
    return false;
  }
}
